import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { cva } from 'class-variance-authority';
import Button from '@/shared/components/elements/Button.jsx';
import { CheckCircle, XCircle, X } from 'lucide-react';

const toastVariants = cva(
  'animate-slide-in-right w-full rounded-lg border bg-white/95 p-3 shadow-lg backdrop-blur-sm transition-all duration-300 sm:max-w-md sm:min-w-[320px] sm:p-4 dark:bg-gray-800/95',
  {
    variants: {
      type: {
        success:
          'border-success-100/50 shadow-success-100/10 dark:border-green-500/50 dark:shadow-green-900/20',
        error:
          'border-error-100/50 shadow-error-100/10 dark:border-red-500/50 dark:shadow-red-900/20',
      },
    },
    defaultVariants: {
      type: 'success',
    },
  },
);

const iconVariants = cva('', {
  variants: {
    type: {
      success: 'text-success-100 dark:text-green-400',
      error: 'text-error-100 dark:text-red-400',
    },
  },
  defaultVariants: {
    type: 'success',
  },
});

const iconComponents = {
  success: CheckCircle,
  error: XCircle,
};

const Toast = ({ message, type = 'success', onClose, duration = 3000 }) => {
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div className={toastVariants({ type })} role="alert">
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          {(() => {
            const Icon = iconComponents[type];
            return <Icon className={iconVariants({ type })} size={18} />;
          })()}
        </div>

        <div className="text-secondary-90 mr-2 min-w-0 flex-1 text-xs font-medium sm:mr-3 sm:text-sm dark:text-gray-100">
          {message}
        </div>

        <div className="flex-shrink-0">
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="transform-none !p-1 !shadow-none hover:scale-100"
            icon={
              <X
                className="text-secondary-50 hover:text-secondary-80 dark:text-gray-400 dark:hover:text-gray-200"
                size={18}
              />
            }
            iconPosition="center"
            aria-label="بستن"
          />
        </div>
      </div>
    </div>
  );
};

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error']),
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.number,
};

export default Toast;
